<template>
    <div>
        <app-header :logo="logoData.logo" :logo-ratio="logoData.logoRatio" :logo-height="logoData.height" :items="navItems" :mobile-items="mobileNavItems" fixed fluid border
                    mobile-menu v-model="isSidebarVisible" class="bg-white"/>

        <app-content>
            <draggable-sidebar v-if="indexStore.areaSections && indexStore.areaSections.length" v-model="isSidebarVisible" v-model:items="indexStore.areaSections" group-prop="title" items-prop="pages"
                               sub-items-prop="pages"
                               :draggable="inEditingMode"
                               :editable="inEditingMode"
							   :color="area.color"
                               width="300"
                               @item-add="handlePageAdd"
                               @item-edit="handlePageEdit"
                               @sub-item-add="handleSubPageAdd"
                               @item-dragged="handleDraggedPage"
                               @sub-item-dragged="handleDraggedSubPage"
                               @item-shifted="handleShiftedPage"
                               @group-deleted="handleDeletedSection"
                               @group-updated="handleUpdatedSection"
                               @group-dragged="handleDraggedSection">

                <template v-slot:item="{ item }">
                    <span>{{ item.title }}</span>
                    <chip v-if="!item.is_published" x-small color="warning" class="ml-8">Unveröffentlicht</chip>
                </template>

                <template v-slot:sub_item="{ item }">
                    <span>{{ item.title }}</span>
                    <chip v-if="!item.is_published" x-small color="warning" class="ml-8">Unveröffentlicht</chip>
                </template>

                <div v-if="indexStore.areaSections && !indexStore.areaSections.length" class="px-16">
                    <missing-info>Noch keine Sektionen vorhanden.</missing-info>
                </div>

                <template #footer>
                    <template v-if="indexStore.hasManagePermission">
                        <div v-if="inEditingMode" class="p-16 border-t">
                            <ajax-form :action="`/v1/guide/${route.params.area}/sections`" method="POST" :data="createSectionData" v-model:loading="creatingSection" reset
                                       @success="handleCreatedSection">
                                <form-input inverted hide-details placeholder="Sektion hinzufügen..." v-model="createSectionData.title" :rules="{required: true}"
                                            :loading="creatingSection"
                                            :disabled="creatingSection"/>
                            </ajax-form>
                        </div>
                        <div class="px-16 border-t pt-16">
                            <form-switch center label="Bearbeitungsmodus" on-text="An" off-text="Aus" v-model="inEditingMode"/>

                            <wrapper class="sm:none mb-4" stretch>
                                <btn to="/" plain color="primary">
                                    <icon :icon="icons.back"/>
                                    <span>Zurück</span>
                                </btn>
                            </wrapper>
                        </div>
                    </template>
                    <div v-else class="p-16 border-t sm:none">
                        <wrapper stretch>
                            <btn to="/" plain color="primary">
                                <icon :icon="icons.back"/>
                                <span>Zurück</span>
                            </btn>
                        </wrapper>
                    </div>
                </template>

            </draggable-sidebar>

            <page-content class="flex-grow-1" :style="{'margin-left': $device.isDesktop ? '300px' : '0px'}" without-mobile-bottom-bar>
                <div class="px-16 lg:px-32 stretch-layout max-w-reading">
					<NuxtPage/>

					<divider/>

					<app-plain-footer class="mb-24"/>
                </div>
            </page-content>
        </app-content>
    </div>
</template>

<script setup lang="ts">

import {Images, ModelType} from "@spoferan/spoferan-ts-core";
import {externalLinkIcon, backIcon} from "@spoferan/nuxt-spoferan/icons";
import {useIndexStore} from "../store";
import {storeToRefs} from "pinia";

const {$web, $apiFetch, $auth, $device} = useNuxtApp();
const router = useRouter();
const route = useRoute();
const {value: cacheBusterValue} = useCacheBuster('area_ts');
const params = route.params;
const indexStore = useIndexStore();
const config = useRuntimeConfig();

const {inEditingMode} = storeToRefs(indexStore)

const responses = await Promise.all([
	useApiFetch(`/v1/guide/${params.area}`, {
		guest: true,
		params: {
			ts: cacheBusterValue
		}
	}),
	initAreaSections()
]);

const area = responses[0].data;
if (area && area._key !== params.area) {
	await navigateTo({params: {area: area._key}}, {
		redirectCode: 308
	});
}

indexStore.area = area;

const createSectionData = ref({
	area: area
});
const creatingSection = ref(false);
const isSidebarVisible = ref($device.isDesktop);

const navItems = [
	{href: config.public.spoferanUrl + '/support', text: 'Support'},
	{href: config.public.spoferanUrl, text: 'SPOFERAN.com', text_icon: externalLinkIcon}
];

const mobileNavItems = [{href: config.public.spoferanUrl, text: 'SPOFERAN.com', text_icon: externalLinkIcon}];

const icons = {
	back: backIcon
}

const logoData = computed(() => {
	if (area.id === 2) {
		return {
			logo: Images.resultastic.logo.path,
			logoRatio: Images.resultastic.logo.ratio,
			height: 30
		}
	}
	if (area.id === 4) {
		return {
			logo: area.image.path,
			logoRatio: area.image.ratio,
			height: 30
		}
	}

	return {
		logo: Images.logo.path,
		logoRatio: Images.logo.ratio,
		height: 26
	}
});

// Refresh permissions on user change as required when user gets authenticated on clients side after SSR hydration
watch(() => $auth.token, () => {
	indexStore.refreshAuthPermissions();
}, {
	immediate: true // Required for CSR
});

watch(inEditingMode, () => {
	initAreaSections();
});

watch(() => route.path, () => {
	if (!$device.isDesktop) {
		isSidebarVisible.value = false;
	}
});

async function initAreaSections (){
	return indexStore.loadAreaSections(params.area, inEditingMode.value, cacheBusterValue).catch(e => {
		console.error(e);
	})
}

function handleCreatedSection(section) {
	indexStore.areaSections.push(section)
}

function handlePageAdd(addEvent) {
	const section = addEvent.group;
	router.push($web('areas.sections.pages.create', [route.params.area, section._key]))
}

function handleSubPageAdd(addEvent) {
	const section = addEvent.group;
	const parentPage = addEvent.item;
	router.push({path: $web('areas.sections.pages.pages.create', [route.params.area, section._key, parentPage._key])})
}

function handlePageEdit(addEvent) {
	const section = addEvent.group;
	const page = addEvent.item;
	router.push($web('areas.sections.pages.edit', [route.params.area, section._key, page._key]))
}

function handleUpdatedSection(updateEvent) {
	const section = updateEvent.group;
	const newTitle = updateEvent.new_value;

	$apiFetch(`/v1/guide/${route.params.area}/sections/${section.id}`, {
		body: {
			title: newTitle
		},
		method: 'put'
	});
}

function handleDeletedSection(deleteEvent) {
	const section = deleteEvent.group;

	$apiFetch(`/v1/guide/${route.params.area}/sections/${section.id}`, {
		method: 'delete'
	});
}

function handleDraggedSection(dragEvent) {
	const section = dragEvent.group;
	const newPosition = dragEvent.new_position - 1; // -1 for latest changes static page

	$apiFetch(`/v1/guide/${route.params.area}/sections/${section.id}`, {
		body: {
			position: newPosition
		},
		method: 'put'
	});
}

function handleDraggedPage(dragEvent) {
	const section = dragEvent.group;
	const page = dragEvent.item;
	const newPosition = dragEvent.new_position;

	$apiFetch(`/v1/guide/${route.params.area}/sections/${section.id}/pages/${page.id}`, {
		body: {
			position: newPosition
		},
		method: 'put'
	});
}

function handleDraggedSubPage(dragEvent) {
	const section = dragEvent.group;
	const parent = dragEvent.parent;
	const page = dragEvent.item;
	const newPosition = dragEvent.new_position;

	$apiFetch(`/v1/guide/${route.params.area}/sections/${section.id}/pages/${parent.id}/${page.id}`, {
		body: {
			position: newPosition
		},
		method: 'put'
	});
}

function handleShiftedPage(dragEvent) {
	const section = dragEvent.new_group;
	const page = dragEvent.item;
	const newPosition = dragEvent.new_position;

	$apiFetch(`/v1/guide/${route.params.area}/sections/${section.id}/pages/${page.id}`, {
		body: {
			position: newPosition,
			section_id: section.id
		},
		method: 'put'
	});
}

useHead({
	// all titles will be injected into this template
	titleTemplate: `%s | ${area.title} Guide`,
});

</script>